import { call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import * as studentTestsTypes from "Store/students/studentsTests/constants";
import { IHandleRescheduleStudentTest } from "Store/students/studentsTests/constants";
import * as globalActions from "Store/global/actions";
import { setActionSpinner, setFetchDetailsSpinner, setFetchSpinner } from "Store/global/actions";
import {
  addBulkStudentAnswersToTestApi,
  addStudentRealTestApi,
  addStudentTestQuestionFlagApi,
  assignTestToStudentApi,
  fetchStudentReportPDFApi,
  fetchStudentTestScoreApi,
  fetchStudentTestSectionProblemsApi,
  fetchStudentTestSectionsApi,
  fetchTestByTestIdApi,
  fetchTestsByStudentIdApi,
  fetchTestsByStudentIdScoredApi,
  fetchTribyteTestSectionApi, getStudentTestReportLinkApi, postStudentTestReportLinkApi,
  postStudentTestScoreApi,
  resetStudentTestsApi,
  updateStudentEssayScoreApi,
  updateStudentRealTestApi,
  updateStudentTestAssignmentDateApi,
  updateStudentTestDueDateApi,
  updateStudentTestLatenessApi, updateStudentTestNotifyApi, updateStudentTestPaceApi,
  updateStudentTestQuestionFlagStatusApi,
  updateStudentTestSectionsApi,
  updateStudentTestSectionStatusApi,
  updateStudentTestStatusApi
} from "Store/students/studentsTests/api";
import { Toast } from "utils/toast";
import ERRORS from "constants/localization/errors";
import SUCCESSES from "constants/localization/successes";
import {
  makeSelectActiveStudent,
  makeSelectActiveStudentTest,
  makeSelectStudentTestSections
} from "Store/students/selectors";
import * as studentTestsActions from "Store/students/studentsTests/actions";
import {
  setEssayScore,
  setStudentScoredTests,
  setStudentTestPreviewPDF, setStudentTestReportData
} from "Store/students/studentsTests/actions";
import { setStudentActionSpinner } from "Store/students/actions";
import { convertMetaToPagination, downloadFile, renderApiParameters } from "Store/global/saga";
import * as globalSelectors from "Store/global/selectors";
import INFOS from "constants/localization/infos";
import { fetchTestSectionTemplatesApi } from "Store/global/api";

function* handleUpdateTestDueDate(action) {
  try {
    const { error } = yield updateStudentTestDueDateApi(action.payload);
    if (error) {
      return Toast.error(ERRORS.handleUpdateTestDueDate, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateTestDueDate, error);
  }
}

function* handleUpdateTestNotify(action) {
  try {
    const { error } = yield updateStudentTestNotifyApi(action.payload);
    if (error) {
      return Toast.error(ERRORS.handleUpdateTestNotify, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateTestNotify, error);
  }
}

function* handleUpdateTestPace(action) {
  try {
    const { error } = yield updateStudentTestPaceApi(action.payload);
    if (error) {
      return Toast.error(ERRORS.handleUpdateTestNotify, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateTestNotify, error);
  }
}

function* handleUpdateTestSectionsSaga(action) {
  try {
    if (!!action.payload.test_section_ids.length) {
      const { error } = yield updateStudentTestSectionsApi(action.payload);
      if (error) {
        return Toast.error(ERRORS.handleUpdateTestSections, error);
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateTestSections, error);
  }
}

function* handleResetStudentTestsSaga(action) {
  yield put(setActionSpinner(true));
  try {
    let errors = [];
    for (let testId of action.payload) {
      const { error } = yield call(resetStudentTestsApi, { student_test_id: testId });
      if (error) {
        errors.push(error);
      }
    }
    if (errors.length) {
      Toast.error(ERRORS.handleResetStudentTests, errors.join(", "));
    } else {
      Toast.success(SUCCESSES.handleResetStudentTests);
    }
    yield put(studentTestsActions.handleFetchStudentTests());
  } catch (error) {
    Toast.error(ERRORS.handleResetStudentTests, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleRescheduleStudentTest(action: IHandleRescheduleStudentTest) {
  yield put(setActionSpinner(true));
  try {
    const { updateRequest, studentId } = action.payload;
    // execute assignment update
    yield call(handleUpdateTestAssignmentDateSaga, {
      payload: {
        assignment_date: updateRequest.assignment_date,
        student_test_id: updateRequest.id
      },
      user: studentId
    });
    yield call(handleUpdateTestDueDate, {
      payload: {
        due_date: updateRequest.due_date,
        student_test_id: updateRequest.id
      },
      user: studentId
    });
    Toast.success(SUCCESSES.handleRescheduleStudentTest);
    yield call(handleFetchStudentTestsSaga);
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleRescheduleStudentTest, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchStudentScoredTestsSaga() {
  try {
    const activeStudent = yield select(makeSelectActiveStudent);
    const { data, error } = yield fetchTestsByStudentIdScoredApi(activeStudent.id, "");
    if (error) {
      Toast.error(ERRORS.handleFetchStudentScoredTestsSaga, error);
    } else {
      yield put(setStudentScoredTests(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudentScoredTestsSaga, error);
  }
}

function* handleUpdateStudentTest(action: studentTestsTypes.IUpdateStudentTest) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const {
      payload: { student_test_id, assignment_date, due_date, test_section_ids, notify, notify_emails, pace },
      testChanges
    } = action;
    const originalTest = action.payload;

    if (testChanges?.assignmentDate && assignment_date) {
      const payload = {
        student_test_id,
        assignment_date: assignment_date
      };
      yield call(handleUpdateTestAssignmentDateSaga, { payload });
    }
    if (testChanges?.dueDate) {
      const payload = {
        student_test_id,
        due_date: due_date
      };
      yield call(handleUpdateTestDueDate, { payload });
    }

    if (testChanges?.notify) {
      const payload = {
        student_test_id,
        notify: notify,
        notify_emails: notify_emails
      };
      yield call(handleUpdateTestNotify, { payload });
    }

    if (testChanges?.pace) {
      const payload = {
        student_test_id,
        pace: pace
      };
      yield call(handleUpdateTestPace, { payload });
    }

    if (
      !originalTest.real_test && !testChanges ? test_section_ids?.length : testChanges?.sections
    ) {
      const payload = {
        student_test_id,
        test_section_ids: test_section_ids
      };
      yield call(handleUpdateTestSectionsSaga, { payload });
    }

    if (originalTest.real_test) {
      const realTestRequest: Partial<RealTest> = {
        student_test_real_id: originalTest.real_test.id,
        link_to_bubble_sheet: originalTest.real_test.link_to_bubble_sheet,
        link_to_test_pdf: originalTest.real_test.link_to_test_pdf,
        personal_note: originalTest.real_test.personal_note,
        student_test_id: originalTest.real_test.student_test_id,
        test_assigned: originalTest.real_test.test_assigned,
        test_due_reminder: originalTest.real_test.test_due_reminder,
        test_subject_ids: originalTest.real_test.test_subject_ids
      };
      const { error: realTestError } = yield updateStudentRealTestApi(realTestRequest);

      for (const section of originalTest.sections) {
        const isScored = originalTest.real_test.test_subject_ids.find(
          (ts) => ts.id === section.section.subject_id
        ).score;

        const payload = {
          student_test_id,
          student_test_section_id: section.id,
          student_test_section_status: isScored ? "COMPLETED" : "CREATED"
        };
        yield updateStudentTestSectionStatusApi(payload);
      }

      if (realTestError) {
        Toast.error(ERRORS.handleUpdateRealStudentTest, realTestError);
      }
    }

    yield call(handleFetchStudentTestsSaga);
    Toast.success(SUCCESSES.handleUpdateStudentTest);
  } catch (error) {
    Toast.error(ERRORS.handleUpdateStudentTest, error);
    yield put(globalActions.setErrorSignal(true));
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleCreateStudentRealTestSaga(action: studentTestsTypes.IHandleCreateStudentRealTest) {
  yield put(globalActions.setActionSpinner(true));
  try {
    const test = action.payload;
    const newTestRequest = {
      assignment_date: test.assignment_date,
      due_date: test.due_date,
      exclude_from_improvement: test.exclude_from_improvement,
      is_timed: test.is_timed,
      student_id: test.student_id,
      test_id: test.test_id,
      test_section_ids: test.test_section_ids
    };
    const { data: testData, error: testError } = yield assignTestToStudentApi(newTestRequest);
    if (testError) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleCreateStudentRealTestSaga, testError);
    }

    const newRealTestRequest = {
      ...test.real_test,
      student_test_id: testData.student_test_id,
      test_subject_ids: test.real_test.test_subject_ids
        .filter((subject) => subject.score)
        .map((subject) =>
          subject.score === ""
            ? {
                ...subject,
                score: null
              }
            : subject
        )
    };

    delete newRealTestRequest.student_id;

    const { data, error } = yield addStudentRealTestApi(newRealTestRequest);
    if (error || !data?.student_test_real_id) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleCreateStudentRealTestSaga, error);
    }
    Toast.success(SUCCESSES.handleCreateStudentRealTestSaga);
    yield call(handleFetchStudentTestsSaga);
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleCreateStudentRealTestSaga, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleUpdateStudentRealTestSaga(action: studentTestsTypes.IHandleUpdateStudentRealTest) {
  yield put(setStudentActionSpinner(true));
  try {
    const { error } = yield updateStudentRealTestApi(action.payload);
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleUpdateStudentRealTestSaga, error);
    }
    Toast.success(SUCCESSES.handleUpdateStudentRealTestSaga);
    yield call(handleFetchStudentTestsSaga);
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleUpdateStudentRealTestSaga, error);
  } finally {
    put(setStudentActionSpinner(false));
  }
}

function* handleAddBulkAnswerToStudentTestSaga(action) {
  try {
    const { data, error } = yield addBulkStudentAnswersToTestApi(action.payload);

    if (error) {
      Toast.error(ERRORS.handleAddBulkAnswerToStudentTestSaga, error);
      yield put(setActionSpinner(false));
    } else {
      Toast.success(SUCCESSES.handleAddBulkAnswerToStudentTestSaga);
    }
    return data;
  } catch (error) {
    Toast.error(ERRORS.handleAddBulkAnswerToStudentTestSaga, error.message);
    yield put(setActionSpinner(false));
  }
}

function* handleExcuseStudentTestLatenessSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { status, error } = yield updateStudentTestLatenessApi({ student_tests: action.payload });

    if (error) {
      Toast.error(ERRORS.handleExcuseStudentTestLatenessSaga, error);
    } else {
      Toast.success(SUCCESSES.handleExcuseStudentTestLatenessSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.handleExcuseStudentTestLatenessSaga, error.message);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleStudentTestSectionManagementSaga(action) {
  yield put(setActionSpinner(true));
  try {
    /**
     * action.payload:
     * bulkTestAnswers: BulkTestAnswersPayload;
     * test_section_id: string;
     * */

    const activeTest = yield select(makeSelectActiveStudentTest);
    const activeStudent = yield select(makeSelectActiveStudent);

    let sectionsStatuses = [...activeTest.sections];

    const activeSection = activeTest.sections.find(
      (section) => section.id === action.payload.test_section_id
    );

    const bulkAnswersResponse = yield call(handleAddBulkAnswerToStudentTestSaga, {
      payload: action.payload.bulkTestAnswers
    });
    if (bulkAnswersResponse?.message) return;

    if (activeSection.test_section_status === "CREATED") {
      const postBody = {
        student_test_id: activeTest.student_test_id,
        student_test_section_id: activeSection.id,
        student_test_section_status: "STARTED"
      };
      const sectionResponse = yield updateStudentTestSectionStatusApi(postBody);

      if (sectionResponse?.message) {
        return Toast.error(ERRORS.handleUpdateTestSections, sectionResponse?.message);
      }
    }

    if (
      activeSection.test_section_status === "CREATED" ||
      activeSection.test_section_status === "STARTED"
    ) {
      const postBody = {
        student_test_id: activeTest.student_test_id,
        student_test_section_id: activeSection.id,
        student_test_section_status: "COMPLETED"
      };
      const sectionResponse = yield updateStudentTestSectionStatusApi(postBody);
      if (sectionResponse?.message) {
        return Toast.error(ERRORS.handleUpdateTestSections, sectionResponse?.message);
      } else {
        sectionsStatuses = activeTest.sections.map((section) =>
          section.id === action.payload.test_section_id
            ? {
                ...section,
                test_section_status: "COMPLETED"
              }
            : section
        );
      }
      // @TODO: Refactor duplicated code
      const sections = yield select(makeSelectStudentTestSections);

      const { data: newProblems, error } = yield fetchStudentTestSectionProblemsApi(
        activeStudent.id,
        activeTest.student_test_id,
        action.payload.test_section_id
      );

      if (error) {
        return Toast.error(ERRORS.fetchStudentTestSectionsRetrieve, error);
      }

      const sectionIndex = sections.findIndex(
        (section) => section.id === action.payload.test_section_id
      );
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        problems: newProblems.problems,
        test_section_status: "COMPLETED"
      };
      yield put(studentTestsActions.setStudentTestSections(sections));
    }
    const isAllSectionsCompleted = sectionsStatuses.every(
      (section) => section.test_section_status === "COMPLETED"
    );

    if (
      (activeTest.status === "STARTED" || activeTest.status === "ASSIGNED") &&
      isAllSectionsCompleted
    ) {
      const { error } = yield updateStudentTestStatusApi({
        student_test_id: activeTest.student_test_id,
        status: "COMPLETED"
      });
      if (error) {
        return Toast.error(ERRORS.handleUpdateTestStatus("COMPLETED"), error);
      } else {
        activeTest.status = "COMPLETED";
      }
    }

    if (activeTest.status === "COMPLETED" || isAllSectionsCompleted) {
      /* Rescoring */
      yield call(handleFetchActiveTestScores, {
        payload: {
          student_id: activeStudent.id,
          student_test_id: activeTest.student_test_id,
          isRealTest: !!activeTest.real_test
        }
      });
      // @TODO: fetch updated problems

      const sections = yield select(makeSelectStudentTestSections);

      const { data: newProblems, error } = yield fetchStudentTestSectionProblemsApi(
        activeStudent.id,
        activeTest.student_test_id,
        action.payload.test_section_id
      );

      if (error) {
        return Toast.error(ERRORS.fetchStudentTestSectionsRetrieve, error);
      }
      const sectionIndex = sections.findIndex(
        (section) => section.id === action.payload.test_section_id
      );

      sections[sectionIndex] = {
        ...sections[sectionIndex],
        problems: newProblems.problems
      };

      yield put(studentTestsActions.setStudentTestSections(sections));
    }

    const { data: testResponse } = yield fetchTestByTestIdApi(
      activeStudent.id,
      activeTest.student_test_id
    );

    yield put(studentTestsActions.setActiveStudentTest(testResponse));
    yield call(handleFetchStudentTestsSaga);

    Toast.success(SUCCESSES.handleStudentTestSectionManagementSaga);
    yield delay(1000);
  } catch (error) {
    Toast.error(ERRORS.handleStudentTestSectionManagementSaga);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUpdateTestAssignmentDateSaga(action) {
  const request = action.payload.length
    ? {
        assignment_date: action.assignment_date,
        student_test_id: action.id
      }
    : action.payload;

  try {
    const { error } = yield updateStudentTestAssignmentDateApi(request);
    if (error) {
      Toast.error(ERRORS.handleUpdateTestAssignmentDate, error);
    }
    if (action.payload.user) {
      yield call(handleFetchStudentTestsSaga);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateTestAssignmentDate, error);
  }
}

export function* handleFetchActiveTestScores(action) {
  yield put(setFetchDetailsSpinner(true));
  try {
    const { student_id, student_test_id, isRealTest } = action.payload;
    if (!isRealTest) {
      yield postStudentTestScoreApi(student_id, student_test_id);
    }

    const { data, error } = yield call(fetchStudentTestScoreApi, student_id, student_test_id);
    if (error) {
      return Toast.error(ERRORS.handleFetchActiveTestScores, error);
    }
    if (!data.essay) {
      data.essay = { analysis: "", reading: "", writing: "" };
    }

    yield put(
      studentTestsActions.setActiveTestScores({
        ...data,
        student_test_id
      })
    );
  } catch (error) {
    Toast.error(ERRORS.handleFetchActiveTestScores, error);
  } finally {
    yield put(setFetchDetailsSpinner(false));
  }
}

export function* handleFetchStudentTestsSaga() {
  yield put(setFetchSpinner(true));
  try {
    const activeStudent = yield select(makeSelectActiveStudent);

    const parameters = yield renderApiParameters(globalSelectors, "studentTests");

    if (activeStudent) {
      const { data, meta, error } = yield fetchTestsByStudentIdApi(activeStudent.id, parameters);
      if (error) {
        return Toast.error(ERRORS.handleFetchStudentTestsSaga, error);
      }
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "studentTests"));
      yield put(studentTestsActions.setStudentTests(data));
    }
  } catch (error) {
    yield put(studentTestsActions.setStudentTests([]));
    Toast.error(ERRORS.handleFetchStudentTestsSaga, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

export function* handleFetchStudentAllTestsSaga() {
  yield put(setFetchSpinner(true));

  try {
    const activeStudent = yield select(makeSelectActiveStudent);
    if (activeStudent) {
      const { data: tests, message } = yield fetchTestsByStudentIdApi(activeStudent.id, "?all=1");

      if (message) {
        return Toast.error(ERRORS.handleFetchStudentTestsSaga, message);
      }

      yield put(studentTestsActions.setStudentTests(tests));
    }
  } catch (error) {
    yield put(studentTestsActions.setStudentTests([]));

    Toast.error(ERRORS.handleFetchStudentTestsSaga, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* fetchStudentTestSectionsSaga(action) {
  const { student_test_id: studentTestId } = action.payload;

  try {
    const activeStudent = yield select(makeSelectActiveStudent);
    yield put(studentTestsActions.setStudentTestSections([]));
    const activeStudentTest = yield select(makeSelectActiveStudentTest);

    if (!activeStudentTest) return null;
    const testSections = activeStudentTest.sections;
    let count = 0;
    while (count < testSections.length) {
      const { data, error } = yield call(
        fetchStudentTestSectionProblemsApi,
        activeStudent.id,
        studentTestId,
        testSections[count].id
      );

      if (error) {
        testSections[count].problems = {
          name: testSections[count].section.name,
          problems: [],
          test_section_id: testSections[count].test_section_id,
          time_limit: 0
        };
        count++;
        Toast.error(ERRORS.fetchStudentTestSectionsRetrieve);
        continue;
      }

      let problems = data.problems;

      if (testSections[count].status !== "COMPLETED") {
        const { data: sectionTemplate, error: templateError } = yield fetchTestSectionTemplatesApi(
          activeStudentTest.test.id,
          testSections[count].section.id
        );

        problems = data.problems.map((studentProblem) => {
          const templateProblem = sectionTemplate.problems.find(
            (tProblem) => tProblem.question_number === studentProblem.question_number
          );
          return {
            ...studentProblem,
            correct_answer: templateProblem.answers
          };
        });
      }

      testSections[count].problems = problems;
      count++;
    }

    yield put(studentTestsActions.setStudentTestSections(testSections));
    // Download PDF is ready once all test section problems have been fetched for the answer sheets
  } catch (error) {
    Toast.error(ERRORS.fetchStudentTestSections, error);
  }
}

function* handleFetchStudentTestReportLink(action) {
  const { student_test_id: studentTestId } = action.payload;

  try {
    const { data, error } = yield postStudentTestReportLinkApi("?student_test_id=" + studentTestId);
    console.log('111', data);
    yield put(studentTestsActions.setStudentTestReportLink({
      jwt: data.jwt
    }));
  } catch (error) {
    Toast.error(ERRORS.fetchStudentTestSections, error);
  }
}
function* handleFetchStudentTestReport(action) {
  const { key: key } = action.payload;

  try {
    const { data, error } = yield getStudentTestReportLinkApi("?key=" + key);
    console.log('111', data);
    yield put(studentTestsActions.setStudentTestReportData({
      data: data
    }));
  } catch (error) {
    Toast.error(ERRORS.fetchStudentTestSections, error);
  }
}

function* handleAssignStudentTestSaga(action) {
  const { payload, source } = action;
  yield put(globalActions.setActionSpinner(true));
  try {
    const { error } = yield call(assignTestToStudentApi, payload);
    if (error) {
      Toast.error(ERRORS.handleAssignStudentTest, error);
      return yield put(globalActions.setErrorSignal(true));
    }
    if (source === "calendar") {
      yield call(handleFetchStudentAllTestsSaga);
    } else {
      yield call(handleFetchStudentTestsSaga);
    }
    Toast.success(SUCCESSES.handleAssignStudentTest);
  } catch (error) {
    Toast.error(ERRORS.handleAssignStudentTest, error);
    yield put(globalActions.setErrorSignal(true));
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleUpdateStudentTestProblemFlagStatusSaga(action) {
  try {
    const { student_test_id, test_section_id, test_problem_id, status, flag } = action.payload;

    const { error } = yield updateStudentTestQuestionFlagStatusApi({
      student_test_id,
      status,
      flag_id: flag.id
    });
    if (error) {
      Toast.error(ERRORS.handleUpdateTestFlagStatus, error);
    }
    let problem_flag_count = 0;
    let reviewed_problems = 0;

    if (status === "FLAGGED" && flag.status === "REVIEWED") {
      problem_flag_count = 1;
      reviewed_problems = -1;
    } else if (status === "UN_FLAGGED" && flag.status === "REVIEWED") {
      reviewed_problems = -1;
    } else if (status === "UN_FLAGGED" && flag.status === "FLAGGED") {
      problem_flag_count = -1;
    }

    yield put(studentTestsActions.updateTestFlagCount({ problem_flag_count, reviewed_problems }));

    const payload = {
      test_section_id,
      test_problem_id,
      flag: {
        id: flag.id,
        status
      }
    };
    yield put(studentTestsActions.setStudentTestProblemFlagStatus(payload));
  } catch (error) {
    Toast.error(ERRORS.handleUpdateTestFlagStatus, error);
  }
}

function* handleAddStudentTestProblemFlagSaga(action) {
  try {
    const { student_test_id, test_section_id, test_problem_id } = action.payload;
    const {
      data: { id },
      error
    } = yield addStudentTestQuestionFlagApi({ student_test_id, test_problem_id });
    if (error) {
      Toast.error(ERRORS.handleAddStudentTestProblemFlagSaga, error);
    } else {
      const flagsCount = {
        problem_flag_count: 1,
        reviewed_problems: 0
      };
      yield put(studentTestsActions.updateTestFlagCount(flagsCount));

      /** minimal data required for next action
       *
       * - test_section_id
       * - test_problem_id
       * - flag
       *
       * */

      const payload = {
        test_section_id,
        test_problem_id,
        flag: {
          id,
          status: "FLAGGED" as FlagStatus
        }
      };

      yield put(studentTestsActions.setStudentTestProblemFlagStatus(payload));
    }
  } catch (error) {
    Toast.error(ERRORS.handleAddStudentTestProblemFlagSaga, error);
  }
}

function* handleMarkTestAllFlagsReviewedSaga(action) {
  try {
    const activeStudent = yield select(makeSelectActiveStudent);
    const { data: sections, error } = yield fetchStudentTestSectionsApi(
      activeStudent.id,
      action.payload[0]
    );

    if (!sections || error) {
      Toast.error(ERRORS.handleMarkAllFlagsReviewed, error);
    }
    const reviewedTestIds = [];
    for (const section of sections) {
      const { data: problems } = yield call(
        fetchStudentTestSectionProblemsApi,
        activeStudent.id,
        action.payload[0],
        section.id
      );

      if (!problems) {
        Toast.error(ERRORS.handleMarkAllFlagsReviewed, "No problems to flag found!");
      }
      for (const problem of problems.problems) {
        const flagData = problem.flag;
        if (flagData.id && flagData.status === "FLAGGED") {
          const payload = {
            status: "REVIEWED",
            flag_id: flagData.id,
            student_test_id: action.studentTestId
          };

          yield call(updateStudentTestQuestionFlagStatusApi, payload);
          reviewedTestIds.push(problem.id);
        }
      }
    }
    yield put(
      studentTestsActions.updateTestFlagCount({ problem_flag_count: 0, reviewed_problems: 1 })
    );
  } catch (error) {
    Toast.error(ERRORS.handleMarkAllFlagsReviewed, "Error while marking all flags reviewed");
  }
}

function* handleFetchStudentReportPDF(action) {
  const { studentId, testId, description, printing, notes } = action.payload;
  !action.preview && Toast.info(INFOS.generatingReportInfo);
  yield put(setActionSpinner(true));
  try {
    const body = {
      description,
      printing,
      notes
    };
    const { data: file, error } = yield fetchStudentReportPDFApi(studentId, testId, body);

    if (error) {
      return Toast.error(ERRORS.handleFetchStudentReportPDF, error);
    }

    if (action.preview) {
      const url = yield window.URL.createObjectURL(file);
      yield put(setStudentTestPreviewPDF(url));
    } else {
      yield call(downloadFile, file, `${action.payload.fileName}-report.pdf`);
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchStudentReportPDF, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUpdateEssayScoreSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield updateStudentEssayScoreApi(action.payload);
    if (error) {
      Toast.error(ERRORS.handleUpdateEssayScoreSaga, error);
    } else {
      yield put(setEssayScore(action.payload.score));
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateEssayScoreSaga, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchTribyteTestSectionSaga(
  action: studentTestsTypes.IHandleFEtchTribyteTestSection
) {
  try {
    yield put(setActionSpinner(true));
    const { test } = action.payload;
    const testSections = test.sections;
    yield call(handleFetchActiveTestScores, {
      payload: {
        student_id: test.student_id,
        student_test_id: test.student_test_id,
        isRealTest: !!test.real_test
      }
    });
    for (const section of testSections) {
      const {
        data: { url },
        error
      } = yield fetchTribyteTestSectionApi({
        student_test_id: section.student_test_id,
        student_test_section_id: section.id
      });
      if (error) {
        return Toast.error(ERRORS.handleFetchTribyteTestSectionSaga, error);
      }
      try {
        yield put(studentTestsActions.setTribyteTestSectionUrl(url, section.id));
      } catch (error) {
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchTribyteTestSectionSaga, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

export default function* studentsTestsSaga() {
  yield takeLatest(studentTestsTypes.HANDLE_RESCHEDULE_STUDENT_TEST, handleRescheduleStudentTest);
  yield takeLatest(studentTestsTypes.UPDATE_STUDENT_TEST, handleUpdateStudentTest);
  yield takeLatest(
    studentTestsTypes.HANDLE_CREATE_STUDENT_REAL_TEST,
    handleCreateStudentRealTestSaga
  );
  yield takeLatest(
    studentTestsTypes.HANDLE_UPDATE_STUDENT_REAL_TEST,
    handleUpdateStudentRealTestSaga
  );
  yield takeLatest(studentTestsTypes.HANDLE_FETCH_STUDENT_TESTS, handleFetchStudentTestsSaga);
  yield takeLatest(
    studentTestsTypes.HANDLE_FETCH_ALL_STUDENT_TESTS,
    handleFetchStudentAllTestsSaga
  );
  yield takeLatest(studentTestsTypes.HANDLE_RESET_STUDENT_TESTS, handleResetStudentTestsSaga);
  yield takeLatest(
    studentTestsTypes.HANDLE_STUDENT_TEST_SECTION_MANAGEMENT,
    handleStudentTestSectionManagementSaga
  );

  yield takeLatest(
    studentTestsTypes.HANDLE_EXCUSE_STUDENT_TEST_LATENESS,
    handleExcuseStudentTestLatenessSaga
  );
  yield takeLatest(
    studentTestsTypes.HANDLE_UPDATE_STUDENT_TEST_PROBLEM_FLAG_STATUS,
    handleUpdateStudentTestProblemFlagStatusSaga
  );
  yield takeLatest(
    studentTestsTypes.HANDLE_ADD_STUDENT_TEST_PROBLEM_FLAG,
    handleAddStudentTestProblemFlagSaga
  );
  yield takeLatest(
    studentTestsTypes.HANDLE_FETCH_STUDENT_SCORED_TESTS,
    handleFetchStudentScoredTestsSaga
  );

  yield takeLatest(studentTestsTypes.FETCH_STUDENT_REPORT_PDF, handleFetchStudentReportPDF);
  yield takeLatest(studentTestsTypes.HANDLE_UPDATE_ESSAY_SCORE, handleUpdateEssayScoreSaga);
  ///= ==========
  yield takeLatest(
      studentTestsTypes.HANDLE_FETCH_STUDENT_TEST_SECTIONS,
      fetchStudentTestSectionsSaga
  );
  yield takeLatest(
      studentTestsTypes.HANDLE_FETCH_STUDENT_TEST_REPORT_LINK,
      handleFetchStudentTestReportLink
  );
  yield takeLatest(
      studentTestsTypes.HANDLE_FETCH_STUDENT_TEST_REPORT,
      handleFetchStudentTestReport
  );

  yield takeLatest(studentTestsTypes.HANDLE_ASSIGN_STUDENT_TEST, handleAssignStudentTestSaga);
  yield takeEvery(
    studentTestsTypes.HANDLE_MARK_TEST_ALL_FLAGS_REVIEWED,
    handleMarkTestAllFlagsReviewedSaga
  );
  yield takeEvery(
    studentTestsTypes.HANDLE_FETCH_TRIBYTE_TEST_SECTION,
    handleFetchTribyteTestSectionSaga
  );
  // yield takeLatest(studentTestsTypes.FETCH_ALL_TESTS, fetchAllTests);
}
